// ---------------------------------------------
// [DSS] Store Picker - Cookie & URL-Param Logic
// ---------------------------------------------
// Single source of truth for the customer's currently selected store.
//
// - Reads `?store=<slug>` from the URL (e.g. ?store=muenchen) and persists it.
// - Persists the chosen store in a first-party cookie (dss_store = <location.id>).
// - Reads the cookie on subsequent visits and re-applies the selection.
// - Auto-selects the store on first visit (no cookie) when the visitor
//   lands on a configured store-landing page (e.g. /pages/18-grad-bike-store-ravensburg
//   sets the store to "ravensburg"). Mapping configurable in the theme editor.
// - Dispatches `store:changed` (CustomEvent) so other modules can react
//   (e.g. pickup-availability re-highlight, analytics tracking).
// - Re-renders the trigger label and active store in the modal without a
//   full page reload. If `reloadOnChange` is set, the page is reloaded so
//   Liquid (e.g. pickup-availability) re-renders from the new cookie.
//
// Selection priority (first match wins, in readInitialState):
//   1. URL-Param `?store=<slug>`   – Google Ads / direct links
//   2. Cookie `dss_store`          – returning visitor preference
//   3. Current page handle match   – first-visit landing on a store page
//   4. Default-mode (specific)     – theme setting
//   5. First default-flagged store
//   6. First store in the list
//
// Data source: shop.locations (rendered into the DOM by dss-header /
// dss-store-modal). The mapping slug <-> id is built in JS at runtime
// from `[data-store-row]` nodes – no Liquid hardcoding required.
// ---------------------------------------------

import { $, $$, debounce } from '../utils/helpers.js';

const STORAGE_KEY = 'dss_store';
const DEFAULT_TTL_DAYS = 365;

function readCookie(name) {
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  // SameSite=Lax so cross-site links from Google Ads can carry the cookie.
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
}

function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

// Safe base64 decoder (returns empty string on bad input or unsupported env).
function decodeBase64(input) {
  if (!input) return '';
  try {
    if (typeof atob === 'function') {
      // Liquid's base64_encode uses standard b64; atob decodes it. We
      // additionally url-safe-decode in case the merchant uses tooling
      // that emits base64url.
      const b64 = String(input).replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(b64);
      // Convert to a UTF-8 string safely.
      try {
        return decodeURIComponent(escape(decoded));
      } catch (_) {
        return decoded;
      }
    }
  } catch (_) {
    // fall through
  }
  return '';
}

// HTML escape helpers for the dynamic modal list (avoids XSS when
// store names / addresses are interpolated into innerHTML).
function escapeText(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
function escapeAttr(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');
}
function stripHtml(s) {
  if (!s) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = s;
  return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
}
function addressToText(s) {
  // Like stripHtml, but preserves <br> as actual newlines so the
  // modal can render multi-line addresses (with `white-space: pre-line`).
  if (!s) return '';
  return s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?p[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Slugify a store name to a stable URL fragment.
// "18° GRAD E-BikeCenter München" -> "18-grad-ebikecenter-muenchen"
export function slugifyStoreName(name) {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export class StorePicker {
  constructor(options = {}) {
    this.root = $('[data-store-picker-root]');
    if (!this.root) return;

    this.options = {
      cookieName: this.root.dataset.cookieName || STORAGE_KEY,
      paramName: this.root.dataset.paramName || 'store',
      reloadOnChange: this.root.dataset.reloadOnChange === 'true',
      autoOpen: this.root.dataset.autoOpen === 'true',
      defaultLocationHandle: this.root.dataset.defaultLocationHandle || '',
      defaultMode: this.root.dataset.defaultMode || 'auto',
      urlAliasesEncoded: this.root.dataset.urlAliases || '',
      pageHandleMappingsEncoded: this.root.dataset.pageHandleMappings || '',
      currentPageHandle: this.root.dataset.currentPageHandle || '',
      ...options,
    };

    // Cookie/identifier strategy: we use the LOCATION SLUG (derived from
    // the store name) instead of the Location ID, because in many dev /
    // preview environments `shop.locations` returns 0 in Liquid even
    // though the API has data. The slug is unique per name, stable
    // across deploys, and works in both Liquid-rendered and
    // API-fetched store lists.
    this.currentId = null;       // backwards-compat: still holds the slug
    this.stores = [];             // [{ id, name, slug, address, phone, hours, ... }]
    this.aliasMap = new Map();    // alias-slug -> store
    this.pageHandleMap = new Map(); // page-handle -> alias slug (Erstbesuch-Mapping)
    this._ready = false;

    this.init();
  }

  init() {
    this.collectStores();
    this.readInitialState();
    this.bindEvents();
    this.render();

    if (this.options.autoOpen && !this.currentId) {
      this.openModal();
    }

    if (window.console && console.info) {
      console.info(
        '[DSS Store Picker] ready –',
        this.stores.length,
        'stores, current:',
        this.currentId || '(none)'
      );
    }

    this.dispatch('store:ready', { id: this.currentId, slug: this.slugFor(this.currentId) });

    // If Liquid didn't render any stores (e.g. dev environment where
    // shop.locations is empty), wait for Dawn's pickup-availability to
    // inject the drawer markup and harvest the data from there. This
    // makes the picker work in BOTH production AND dev / preview.
    if (this.stores.length === 0) {
      this.harvestStoresFromDawnDrawer();
    }

    // Dawn's pickup-availability.js injects the drawer markup asynchronously.
    // Watch the DOM so the row tagging re-runs when new rows appear.
    this.observePickupAvailability();
  }

  /**
   * Dawn injects location data into the page via its own pickup-availability
   * component (which uses Shopify's admin API on-demand). When our Liquid
   * template is empty (e.g. dev / preview), we wait for those rows to
   * appear and re-use them to populate the picker modal + sync the
   * Dawn preview with the cookie selection.
   */
  harvestStoresFromDawnDrawer() {
    if (typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => {
      const rows = $$('.pickup-availability-list__item', document);
      if (!rows.length) return;
      observer.disconnect();

      const harvested = rows
        .map((row) => {
          const heading = row.querySelector('h3');
          if (!heading) return null;
          const name = heading.textContent.trim();
          if (!name) return null;
          const slug = slugifyStoreName(name);
          if (this.stores.some((s) => s.slug === slug)) return null;
          return {
            id: slug,
            slug,
            name,
            address: '',
            phone: '',
            hours: '',
            distance: '',
            isDefault: false,
            node: null,
            fromDawn: true,
          };
        })
        .filter(Boolean);

      if (!harvested.length) return;

      this.stores = this.stores.concat(harvested);
      this.parseAliases();
      this.parsePageHandleMappings();
      // Build the modal <li>s from the harvested stores (the Liquid
      // version was empty because shop.locations.size was 0).
      this.renderList();
      // Re-read URL/cookie/default now that we have data. This may
      // restore the customer's previous selection without opening
      // the modal.
      this.readInitialState({ silent: true });
      this.render();

      // Only surface the modal automatically if the user has no
      // pre-existing selection (no cookie, no URL param, no default).
      // Otherwise respect their choice and stay quiet.
      if (!this.currentId && this.options.autoOpen) {
        this.openModal();
      }

      if (window.console && console.info) {
        console.info(
          '[DSS Store Picker] harvested',
          harvested.length,
          'stores from Dawn drawer'
        );
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  observePickupAvailability() {
    if (typeof MutationObserver === 'undefined') return;
    const target = document.body;
    // Debounce at 150ms – Dawn's variant picker can fire several
    // mutations in quick succession (option text update + preview
    // replace). Without debouncing we get a per-rAF feedback loop
    // because our override mutates the DOM.
    let debounceId = 0;
    const observer = new MutationObserver(() => {
      if (debounceId) return;
      debounceId = setTimeout(() => {
        debounceId = 0;
        // Dawn re-renders the inline pickup-availability preview
        // asynchronously (initial fetch + variant change). We re-apply
        // the inline-preview override so the customer's selected
        // store + correct availability icon stay in place.
        //
        // IMPORTANT: We do NOT touch Dawn's <pickup-availability-drawer>
        // here. The drawer must remain 1:1 as Dawn renders it – our
        // store-picker is only allowed to influence the inline preview
        // inside the buybox.
        this.overrideDawnPreview();
      }, 150);
    });
    observer.observe(target, { childList: true, subtree: true });
  }

  collectStores() {
    // Source 1: each store row carries its data on data-* attributes
    // – populated by Liquid in production (where shop.locations is
    // always available) and by JS in dev (via the Dawn-drawer
    // harvest).
    this.stores = $$('[data-store-row]', this.root).map((node) => ({
      id: node.dataset.storeId,
      name: node.dataset.storeName || '',
      slug: node.dataset.storeSlug || slugifyStoreName(node.dataset.storeName || ''),
      address: node.dataset.storeAddress || '',
      phone: node.dataset.storePhone || '',
      hours: node.dataset.storeHours || '',
      distance: node.dataset.storeDistance || '',
      isDefault: node.dataset.storeIsDefault === 'true',
      node,
    }));

    // Source 2: a <script type="application/json" data-store-locations>
    // block is rendered on every page (it's part of the modal markup,
    // which is in the header on every layout). This is the primary
    // source on non-PDPs where Dawn's pickup-availability drawer
    // doesn't exist. Falls back silently if the script block is empty
    // (e.g. dev environments where shop.locations.size is 0).
    try {
      const dataEl = $('[data-store-locations]', this.root);
      if (dataEl) {
        const raw = (dataEl.textContent || '').trim();
        if (raw && raw !== '[]') {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            // Defensive: a legacy / mis-rendered Liquid block can
            // produce a double-wrapped array `[[{...}]]` (the
            // snippet's own outer brackets + the user's array
            // already wrapped in [...]). JSON.parse hands back
            // `[[{...}]]` which forEach would skip entirely –
            // arrays don't have `.name`. Flatten so the rest of
            // the merge logic stays unaware of the wrapping.
            const flat =
              parsed.length === 1 && Array.isArray(parsed[0])
                ? parsed[0]
                : parsed;
            flat.forEach((entry) => {
              if (!entry || !entry.name) return;
              const slug =
                entry.slug || slugifyStoreName(entry.name);
              // Don't duplicate if Liquid already added this store.
              if (this.stores.some((s) => s.slug === slug)) return;
              this.stores.push({
                id: entry.id || slug,
                slug,
                name: entry.name || '',
                address: entry.address || '',
                phone: entry.phone || '',
                hours: entry.hours || '',
                distance: '',
                isDefault: false,
                node: null,
                fromJson: true,
              });
            });
          }
        }
      }
    } catch (e) {
      // Invalid JSON in the script block – fall through silently.
    }

    this.parseAliases();
    this.parsePageHandleMappings();
    this.renderList();
  }

  /**
   * Render the store list into the modal `<ul>`. Idempotent: wipes
   * existing children and re-builds from `this.stores`. Liquid may
   * have pre-rendered some rows (production) and JS will then add any
   * harvested ones (dev / preview) on top.
   */
  renderList() {
    const list = $('[data-store-list]', this.root);
    const empty = $('[data-store-empty]', this.root);
    if (!list) return;

    // Keep any rows that are already in the DOM (from Liquid) and
    // append missing ones from this.stores.
    const existingSlugs = new Set(
      $$('[data-store-row]', list).map((row) => row.dataset.storeSlug)
    );

    this.stores.forEach((store, index) => {
      if (existingSlugs.has(store.slug)) return;

      const li = document.createElement('li');
      li.className = 'dss-store-row';
      li.setAttribute('data-store-row', '');
      li.setAttribute('data-store-picker-select', store.id);
      li.setAttribute('data-store-id', store.id);
      li.setAttribute('data-store-name', store.name);
      li.setAttribute('data-store-slug', store.slug);
      li.setAttribute('data-store-address', store.address || '');
      li.setAttribute('data-store-phone', store.phone || '');
      li.setAttribute('data-store-hours', store.hours || '');
      li.setAttribute(
        'data-store-is-default',
        store.isDefault ? 'true' : 'false'
      );
      if (store.fromDawn) li.setAttribute('data-store-from-dawn', 'true');

      li.innerHTML = `
        <button type="button" class="dss-store-row__button" data-store-picker-select="${escapeAttr(store.id)}">
          <div class="dss-store-row__main">
            <div class="dss-store-row__name">
              <span>${escapeText(store.name)}</span>
            </div>
            ${
              store.address
                ? `<p class="dss-store-row__address">${escapeText(addressToText(store.address))}</p>`
                : ''
            }
            ${
              store.phone || store.hours
                ? `<div class="dss-store-row__meta">${
                    store.phone
                      ? `<span class="dss-store-row__meta-item"><span class="svg-wrapper"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" stroke-linecap="round" stroke-linejoin="round"/></svg></span><span>${escapeText(store.phone)}</span></span>`
                      : ''
                  }${
                    store.hours
                      ? `<span class="dss-store-row__meta-item"><span class="svg-wrapper"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14" stroke-linecap="round" stroke-linejoin="round"/></svg></span><span>${escapeText(store.hours)}</span></span>`
                      : ''
                  }</div>`
                : ''
            }
          </div>
          <span class="dss-store-row__check" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
              <polyline points="4 11 8 15 16 5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>
      `;
      list.appendChild(li);
    });

    // Toggle empty state visibility
    if (empty) {
      const visible = $$('[data-store-row]', list).length === 0;
      empty.hidden = !visible;
    }
  }

  parseAliases() {
    // Format: one mapping per line, "slug:LocationName". The LocationName
    // is matched (case-insensitive, trimmed) against the registered stores.
    // Encoded as base64 in the data attribute so newlines survive the
    // HTML roundtrip.
    this.aliasMap = new Map();
    const raw = decodeBase64(this.options.urlAliasesEncoded);
    if (!raw) return;

    raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const sep = line.indexOf(':');
        if (sep < 0) return;
        const alias = line.slice(0, sep).trim().toLowerCase();
        const name = line.slice(sep + 1).trim();
        if (!alias || !name) return;

        // Match by name (case-insensitive). The first match wins; later
        // lines for the same alias are silently ignored.
        const target = this.stores.find(
          (s) => s.name.toLowerCase() === name.toLowerCase()
        );
        if (target && !this.aliasMap.has(alias)) {
          this.aliasMap.set(alias, target);
        }
      });
  }

  /**
   * Parse the "Store-Seiten → Filiale" mapping from settings.
   * Format: one mapping per line, "page-handle:store-slug".
   *
   *   18-grad-e-bike-center-muenchen:munich
   *   18-grad-bike-store-ravensburg:ravensburg
   *
   * When a visitor lands on `/pages/<page-handle>` for the first time
   * (no cookie, no ?store= param), the matching store is auto-selected
   * (analogous to ?store=<slug>). Existing user preferences are
   * preserved – the cookie always wins on subsequent visits.
   *
   * The store-slug is resolved via the same lookup as URL aliases, so
   * both custom aliases (e.g. "munich") and auto-generated name slugs
   * (e.g. "18-grad-e-bikecenter-muenchen") work.
   */
  parsePageHandleMappings() {
    this.pageHandleMap = new Map();
    const raw = decodeBase64(this.options.pageHandleMappingsEncoded);
    if (!raw) return;

    raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const sep = line.indexOf(':');
        if (sep < 0) return;
        const handle = line.slice(0, sep).trim().toLowerCase();
        const slug = line.slice(sep + 1).trim().toLowerCase();
        if (!handle || !slug) return;
        // First entry wins per handle.
        if (!this.pageHandleMap.has(handle)) {
          this.pageHandleMap.set(handle, slug);
        }
      });
  }

  /**
   * Look up the store that should be auto-selected for the current
   * page handle. Returns `null` if either no current page handle is
   * set, no mapping matches, or the mapped slug doesn't resolve to a
   * known store. Used by `readInitialState()` as the first-visit
   * fallback (only when neither URL-param nor cookie is present).
   */
  findStoreByCurrentPage() {
    const handle = (this.options.currentPageHandle || '').toLowerCase().trim();
    if (!handle) return null;
    if (!this.pageHandleMap.has(handle)) return null;
    return this.findStoreBySlug(this.pageHandleMap.get(handle));
  }

  findStoreBySlug(slug) {
    if (!slug) return null;
    const normalized = String(slug).toLowerCase().trim();

    // 1) Custom URL alias (e.g. "munich" from Google Ads)
    if (this.aliasMap.has(normalized)) {
      return this.aliasMap.get(normalized);
    }

    // 2) Auto-generated slug from the location name
    return this.stores.find((s) => s.slug === normalized) || null;
  }

  readInitialState({ silent = false } = {}) {
    // 1) URL-Param wins (always re-applies so a new ad click can override
    //    the cached cookie – this is the Google Ads mechanism). Custom
    //    URL aliases (e.g. "munich") are matched before the auto-slug.
    const params = new URLSearchParams(window.location.search);
    const slug = params.get(this.options.paramName);

    if (slug) {
      const match = this.findStoreBySlug(slug);
      if (match) {
        this.setStore(match.slug, { silent, stripParam: true });
        return;
      }
    }

    // 2) Cookie (stores the slug, not the location id, for portability
    //    across dev environments where `shop.locations` returns 0).
    const cookieVal = readCookie(this.options.cookieName);
    if (cookieVal) {
      const target = this.findStoreBySlug(cookieVal);
      if (target) {
        this.setStore(target.slug, { silent });
        return;
      }
    }

    // 3) First-visit auto-selection from the current page handle (e.g.
    //    landing directly on `/pages/18-grad-e-bike-center-muenchen`
    //    sets the store to "munich"). Only triggers when there's no
    //    cookie yet, so a returning visitor's preference is preserved
    //    even when they later browse to another store's page.
    //    The cookie is written by setStore() so the selection persists
    //    across subsequent navigation.
    const pageMatch = this.findStoreByCurrentPage();
    if (pageMatch) {
      this.setStore(pageMatch.slug, { silent });
      return;
    }

    // Update trigger has-selection dot indicator (shown when a
    // default store is currently active).
    $$('[data-store-picker-open]').forEach((trigger) => {
      trigger.setAttribute('data-has-selection', 'false');
    });

    // 4) Default (specific handle from settings – matches both custom
    //    aliases and auto-slugs via findStoreBySlug).
    if (
      this.options.defaultMode === 'specific' &&
      this.options.defaultLocationHandle
    ) {
      const target = this.findStoreBySlug(this.options.defaultLocationHandle);
      if (target) {
        this.setStore(target.slug, { silent, persistCookie: false });
        return;
      }
    }

    // 5) Default (first store marked as default in the markup)
    const flagged = this.stores.find((s) => s.isDefault);
    if (flagged) {
      this.setStore(flagged.slug, { silent, persistCookie: false });
      return;
    }

    // 6) Fallback: first store
    if (this.stores.length) {
      this.setStore(this.stores[0].slug, { silent, persistCookie: false });
    }
  }

  bindEvents() {
    // Open triggers
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-store-picker-open]');
      if (trigger) {
        e.preventDefault();
        this.openModal();
      }
    });

    // Close triggers
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-store-picker-close]')) {
        e.preventDefault();
        this.closeModal();
      }
    });

    // Backdrop click (only when click is directly on backdrop)
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-store-picker-backdrop]')) {
        this.closeModal();
      }
    });

    // Store selection
    document.addEventListener('click', (e) => {
      const row = e.target.closest('[data-store-picker-select]');
      if (row) {
        e.preventDefault();
        const id = row.dataset.storePickerSelect;
        if (id) this.selectStore(id);
      }
    });

    // ESC closes the modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.closeModal();
      }
    });

    // React to programmatic changes from other parts of the page
    // (e.g. pickup-availability drawer "andere Filiale" button).
    document.addEventListener('store:request', (e) => {
      this.openModal();
    });
  }

  selectStore(id) {
    // `id` is either a Shopify Location ID (from Liquid rows) or a slug
    // (from harvested Dawn drawer rows). Resolve to the actual store.
    const store =
      this.stores.find((s) => s.id === id) || this.findStoreBySlug(id);
    if (!store) return;
    this.setStore(store.slug, { reload: this.options.reloadOnChange });
    this.closeModal();
  }

  setStore(slug, { silent = false, reload = false, stripParam = false, persistCookie = true } = {}) {
    this.currentId = slug;

    if (persistCookie) {
      writeCookie(this.options.cookieName, slug, DEFAULT_TTL_DAYS);
    }

    if (stripParam) {
      this.stripUrlParam();
    }

    if (reload) {
      // Reload the page so Liquid re-renders pickup-availability against
      // the new cookie. The brief flicker is acceptable for a one-tap
      // store change and ensures server-rendered state stays consistent.
      window.location.reload();
      return;
    }

    this.render();

    if (!silent) {
      this.dispatch('store:changed', { id: slug, slug });
    }
  }

  stripUrlParam() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(this.options.paramName);
      const next = url.pathname + (url.search ? url.search : '') + url.hash;
      window.history.replaceState({}, document.title, next);
    } catch (_) {
      // no-op for browsers without URL API
    }
  }

  render() {
    // Trigger labels – look up the current store by slug (or by id for
    // backwards-compat with harvested stores that share id == slug).
    const current =
      this.stores.find((s) => s.slug === this.currentId) ||
      this.stores.find((s) => s.id === this.currentId);
    $$('[data-store-picker-current-name]').forEach((el) => {
      el.textContent = current ? current.name : '';
    });
    $$('[data-store-picker-current-slug]').forEach((el) => {
      el.textContent = current ? current.slug : '';
    });
    $$('[data-store-picker-current-address]').forEach((el) => {
      el.textContent = current ? this.formatAddress(current.address) : '';
    });

    // Update row .is-active + the wrapping <button> for the active highlight.
    // Match against both the row's data-store-id (Liquid rendered) and
    // the row's data-store-slug (harvested from Dawn drawer).
    $$('[data-store-row]', this.root).forEach((row) => {
      const isActive =
        row.dataset.storeId === this.currentId ||
        row.dataset.storeSlug === this.currentId;
      row.classList.toggle('is-active', isActive);
      row.setAttribute('aria-current', isActive ? 'true' : 'false');
      const btn = row.querySelector('.dss-store-row__button');
      if (btn) btn.classList.toggle('is-active', isActive);
    });

    // Mark body for any CSS hookups
    document.documentElement.dataset.dssStore = this.currentId || '';

    // Override Dawn's INLINE pickup-availability preview so the customer
    // sees the SELECTED store – not Dawn's "closest" pick (which is
    // usually wrong when a store is pinned via ?store= or cookie).
    //
    // We intentionally do NOT touch Dawn's <pickup-availability-drawer>
    // – that dropdown must remain 1:1 as Dawn renders it.
    this.overrideDawnPreview();
  }

  /**
   * Dawn's `<pickup-availability-preview>` shows the "closest" location
   * to the visitor (IP-based). Once we know which store the customer
   * actually wants, replace the preview's content with the matching
   * row from the drawer. This is non-destructive – if we can't find
   * the matching row, Dawn's original preview is kept.
   */
  overrideDawnPreview() {
    if (!this.currentId) return;
    const current =
      this.stores.find((s) => s.slug === this.currentId) ||
      this.stores.find((s) => s.id === this.currentId);
    if (!current) return;

    const preview = $('pickup-availability-preview');
    if (!preview) return;

    // Look up the drawer rows. If the product is available somewhere,
    // the drawer will list it. The user's preferred store may or may
    // not be in stock – we surface that distinction in the preview.
    const drawerRows = $$('.pickup-availability-list__item');
    if (!drawerRows.length) return; // Dawn drawer not loaded yet

    const rowForName = (name) =>
      drawerRows.find((row) => {
        const heading = row.querySelector('h3');
        return heading && heading.textContent.trim() === name;
      });

    const preferredRow = rowForName(current.name);
    const preferredIsAvailable = !!(
      preferredRow && preferredRow.classList.contains('available')
    );

    const extractPostalCode = (value) => {
      if (!value) return null;
      const text = String(value)
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const match = text.match(/\b(\d{5})\b/);
      if (!match) return null;
      const postalCode = Number(match[1]);
      return Number.isFinite(postalCode) ? postalCode : null;
    };

    const preferredPostalCode = (() => {
      const fromCurrentStore = extractPostalCode(current.address);
      if (fromCurrentStore != null) return fromCurrentStore;

      const preferredAddress = preferredRow
        ? preferredRow.querySelector('address')
        : null;
      return preferredAddress
        ? extractPostalCode(preferredAddress.textContent || '')
        : null;
    })();

    const pickClosestAvailableRowByPostalCode = () => {
      const availableRows = drawerRows.filter((row) =>
        row.classList.contains('available')
      );
      if (!availableRows.length) return null;

      // If we cannot determine the preferred postal code, keep the
      // current behavior and use Dawn's first available row order.
      if (preferredPostalCode == null) return availableRows[0];

      let best = null;

      availableRows.forEach((row, index) => {
        const heading = row.querySelector('h3');
        const name = heading ? heading.textContent.trim() : '';
        const store = this.stores.find((s) => s.name === name);
        const rowAddress = row.querySelector('address');

        const candidatePostalCode =
          (store && extractPostalCode(store.address)) ||
          (rowAddress && extractPostalCode(rowAddress.textContent || ''));

        const postalDifference =
          candidatePostalCode != null
            ? Math.abs(candidatePostalCode - preferredPostalCode)
            : Number.POSITIVE_INFINITY;

        if (
          !best ||
          postalDifference < best.postalDifference ||
          (postalDifference === best.postalDifference && index < best.index)
        ) {
          best = { row, index, postalDifference };
        }
      });

      return best ? best.row : availableRows[0];
    };

    // Determine which store to show in the preview
    let displayStore = current;
    let displayRow = preferredRow;
    let isFallback = false;

    if (!preferredIsAvailable) {
      // The user's preferred store has no stock of this product.
      // Find the available store with the smallest absolute postal-code
      // difference to the preferred store, and use that for the preview.
      // The cookie / user preference is NOT changed – the user can
      // still see the picker with their preference highlighted.
      const firstAvailableRow = pickClosestAvailableRowByPostalCode();
      if (firstAvailableRow) {
        const name = firstAvailableRow
          .querySelector('h3')
          .textContent.trim();
        const store = this.stores.find((s) => s.name === name);
        if (store) {
          displayStore = store;
          displayRow = firstAvailableRow;
          isFallback = true;
        }
      } else if (!preferredRow) {
        // No matching row in the drawer at all = product not stocked
        // anywhere we know about. Fall back to Dawn's original preview.
        return;
      }
    }

    // ----- Availability + data for the DISPLAYED store -----
    const isAvailable = !!(
      displayRow && displayRow.classList.contains('available')
    );
    const pickUpTimeEl = displayRow
      ? displayRow.querySelector('.pickup-availability-preview')
      : null;
    const pickUpTime = pickUpTimeEl
      ? pickUpTimeEl.textContent.replace(/\s+/g, ' ').trim()
      : '';
    const address = displayRow ? displayRow.querySelector('address') : null;

    // Single-line title that fuses the pickup time (when present)
    // into the same sentence. Dawn's `pickUpTime` already starts with
    // "Abholung …" – we strip that prefix so the combined line reads
    //   "Abholung in <STORE> verfügbar, gewöhnlich fertig in 24 Stunden"
    // instead of "Abholung in <STORE> Abholung verfügbar, …".
    //
    // Also: Dawn hardcodes "stunden" (lowercase) inside its
    // pickup-availability.js bundle. In German, "Stunde" is a noun
    // and must be capitalised – we patch it back here regardless of
    // what Dawn's translation file says.
    const timeText = pickUpTime
      ? pickUpTime
          .replace(/^Abholung\s+/i, '')
          .replace(/(\d+)\s+stunden/gi, '$1 Stunden')
          .trim()
      : '';
    const expectedTitle = isAvailable
      ? timeText
        ? `Abholung in ${displayStore.name} ${timeText}`
        : `Abholung in ${displayStore.name}`
      : isFallback
        ? `Derzeit nicht verfügbar`
        : `Derzeit nicht verfügbar in ${displayStore.name}`;

    // ----- Idempotency guard: bail out if we already applied this exact
    // override. Without this, the MutationObserver that re-invokes us
    // after Dawn re-renders would trigger a feedback loop (we mutate
    // DOM → observer fires → we mutate again → …).
    const expectedSlug = `${displayStore.slug}|${
      isFallback ? 'fallback' : 'preferred'
    }`;
    // Use querySelectorAll + remove all: if a race condition with
    // Dawn's renderPreview left multiple .pickup-availability-info
    // blocks in the preview, we collapse them down to a single one
    // before re-applying.
    const existingInfos = preview.querySelectorAll(
      '.pickup-availability-info'
    );
    let existingInfo = null;
    let matchesExpected = false;
    existingInfos.forEach((node) => {
      if (
        !existingInfo &&
        node.dataset.dssStorePicker === expectedSlug
      ) {
        existingInfo = node;
        matchesExpected = true;
      } else {
        node.remove();
      }
    });
    if (matchesExpected && existingInfo) {
      const existingTitle = existingInfo.querySelector('.caption-large');
      if (
        existingTitle &&
        existingTitle.textContent.trim() === expectedTitle
      ) {
        return; // Already up to date
      }
    }

    // Flip the status icon (tick / unavailable) on the preview
    // wrapper so it visually matches the available state.
    const iconWrap = preview.querySelector('.svg-wrapper');
    if (iconWrap) {
      iconWrap.innerHTML = isAvailable
        ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-tick" viewBox="0 0 20 20"><path fill="#108043" stroke="#fff" d="m16.136 6.979.003-.004a1.499 1.499 0 0 0-2.275-1.954l-5.945 6.777-1.858-1.859A1.499 1.499 0 1 0 3.94 12.06l2.999 3c.289.29.675.44 1.1.439h.019a1.5 1.5 0 0 0 1.08-.522z"></path></svg>'
        : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-unavailable" viewBox="0 0 20 20"><path fill="#EB001B" stroke="#fff" d="M14.7 5.3a1 1 0 0 0-1.4 0L10 8.6 6.7 5.3a1 1 0 0 0-1.4 1.4L8.6 10l-3.3 3.3a1 1 0 0 0 1.4 1.4L10 11.4l3.3 3.3a1 1 0 0 0 1.4-1.4L11.4 10l3.3-3.3a1 1 0 0 0 0-1.4z"></path></svg>';
    }

    // ----- Rebuild the preview body -----

    const info = document.createElement('div');
    info.className = 'pickup-availability-info';
    info.dataset.dssStorePicker = expectedSlug; // idempotency marker

    // Order:
    //   1. Fallback-note (WHY we're showing this store – only if fallback)
    //   2. Title ("Abholung in X" / "Derzeit nicht verfügbar")
    //   3. Pickup time (e.g. "Abholung verfügbar, gewöhnlich fertig in 24 stunden")
    //   4. Address
    //   5. "Andere Filialen anzeigen" button
    //
    // The fallback note is shown FIRST so the customer understands
    // why the displayed store differs from their preference (when
    // applicable) – otherwise the title looks disconnected.
    if (isFallback) {
      const noteEl = document.createElement('p');
      noteEl.className = 'caption dss-store-preview__fallback-note';
      noteEl.appendChild(document.createTextNode('Dein'));

      const preferredStoreLink = document.createElement('button');
      preferredStoreLink.type = 'button';
      preferredStoreLink.className = 'link link--text underlined-link';
      preferredStoreLink.setAttribute('data-store-picker-open', '');
      preferredStoreLink.textContent = `bevorzugter Store`;

      noteEl.appendChild(preferredStoreLink);
      noteEl.appendChild(
        document.createTextNode(
          `${current.name} hat dieses Produkt nicht auf Lager. Wir zeigen dir den nächstgelegenen verfügbaren Store.`
        )
      );
      info.appendChild(noteEl);
    }

    const titleEl = document.createElement('p');
    titleEl.className = 'caption-large';
    if (isAvailable) {
      // Green pulsing dot signals "this store has stock".
      const pulse = document.createElement('span');
      pulse.className =
        'dss-availability__pulse dss-availability__pulse--available';
      pulse.setAttribute('aria-hidden', 'true');
      titleEl.appendChild(pulse);
      titleEl.appendChild(document.createTextNode(' ' + expectedTitle));
    } else {
      // Red dot signals "currently not available".
      const pulse = document.createElement('span');
      pulse.className =
        'dss-availability__pulse dss-availability__pulse--unavailable';
      pulse.setAttribute('aria-hidden', 'true');
      titleEl.appendChild(pulse);
      titleEl.appendChild(document.createTextNode(' ' + expectedTitle));
    }
    info.appendChild(titleEl);

    if (address) {
      // Compact single-line address: drop the "Deutschland" line,
      // join the remaining address lines with ", ", and put the
      // phone on its own line. Dawn's default layout is too
      // vertical (4 lines for München) – merchants prefer the
      // one-line format.
      const compact = this.formatCompactAddress(address);
      if (compact.address || compact.phone) {
        const addrEl = document.createElement('div');
        addrEl.className = 'caption dss-store-preview__address';
        if (compact.address) {
          const line1 = document.createElement('p');
          line1.textContent = compact.address;
          addrEl.appendChild(line1);
        }
        if (compact.phone) {
          // Phone on its own line, with a small inline icon (same
          // path as the modal's meta-item icon for visual consistency).
          const line2 = document.createElement('p');
          line2.className = 'dss-store-preview__phone';
          line2.innerHTML =
            '<span class="svg-wrapper" aria-hidden="true">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
                'stroke-width="2">' +
                '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-' +
                  '8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-' +
                  '3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.' +
                  '37 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6' +
                  'l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.85.57 2.81.7A2 2 0 ' +
                  '0 1 22 16.92z" stroke-linecap="round" stroke-linejoin="round"/>' +
              '</svg>' +
            '</span>' +
            '<span>' + compact.phone + '</span>';
          addrEl.appendChild(line2);
        }
        info.appendChild(addrEl);
      }
    }

    // "Andere Filialen anzeigen" — opens Dawn's drawer via the public
    // `show()` method. We intentionally do NOT click the (now removed)
    // internal ShowPickupAvailabilityDrawer button, since Dawn expects
    // the drawer to be opened via its own API.
    //
    // CRITICAL: The button carries `id="ShowPickupAvailabilityDrawer"`
    // so Dawn's body-click handler (which closes the drawer when the
    // click target is outside the drawer) treats it as the opener and
    // does NOT close the freshly opened drawer.
    const otherBtn = document.createElement('button');
    otherBtn.className = 'pickup-availability-button link link--text underlined-link';
    otherBtn.type = 'button';
    otherBtn.id = 'ShowPickupAvailabilityDrawer';
    otherBtn.textContent = 'Verfügbarkeit in allen Filialen anzeigen';
    otherBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const drawer = document.querySelector('pickup-availability-drawer');
      if (drawer && typeof drawer.show === 'function') {
        drawer.show(otherBtn);
      } else {
        // Fallback: try the original Dawn trigger if it still exists
        const trigger = $('#ShowPickupAvailabilityDrawer');
        if (trigger) trigger.click();
      }
    });
    info.appendChild(otherBtn);

    preview.appendChild(info);
  }

  formatAddress(html) {
    if (!html) return '';
    // Strip HTML tags and collapse whitespace for plain-text usage in info row.
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Parse Dawn's <address class="pickup-availability-address"> into
   * a compact one-line format suitable for the inline preview:
   *   - All address lines (street / address2 / postal+city) joined
   *     with ", ".
   *   - The country line (Deutschland / Germany / Österreich / …)
   *     is dropped.
   *   - The phone number ends up on its own line.
   *
   * Example:
   *   Input:  <p>Schwanenstraße 50<br>88214 Ravensburg<br>Deutschland</p>
   *           <p>+4975136660280</p>
   *   Output: { address: "Schwanenstraße 50, 88214 Ravensburg",
   *             phone: "+4975136660280" }
   */
  formatCompactAddress(addressEl) {
    const result = { address: '', phone: '' };
    if (!addressEl) return result;

    const addressLines = [];
    let phone = '';

    addressEl.querySelectorAll('p').forEach((p) => {
      // Walk the <p>'s children to split on <br> without losing order.
      const parts = [];
      p.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          parts.push(node.textContent);
        } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
          parts.push('\n');
        }
      });
      const text = parts.join('').replace(/\s+/g, ' ').trim();

      // Phone detection: starts with + or is mostly digits (allow
      // spaces, dashes, parens). We previously guarded with
      // `!addressLines.length` which silently broke Dawn's layout
      // (phone is always the SECOND <p>, so addressLines is already
      // populated when we get here). The regex alone is enough.
      if (/^\+?[\d][\d\s\-()]{4,}$/.test(text)) {
        phone = text;
      } else if (text) {
        text.split('\n').forEach((line) => {
          const trimmed = line.trim();
          if (trimmed) addressLines.push(trimmed);
        });
      }
    });

    // Drop the country line if it's the last one. Covers DE/AT/CH
    // out of the box; add more as the merchant expands.
    const countries = new Set([
      'Deutschland',
      'Germany',
      'Österreich',
      'Austria',
      'Schweiz',
      'Switzerland',
    ]);
    if (
      addressLines.length &&
      countries.has(addressLines[addressLines.length - 1])
    ) {
      addressLines.pop();
    }

    result.address = addressLines.join(', ');
    result.phone = phone;
    return result;
  }

  openModal() {
    const modal = $('[data-store-picker-modal]', this.root);
    if (!modal) return;
    modal.classList.add('is-open');
    document.body.classList.add('dss-store-picker--open');
    // Focus first focusable element (the close button is a safe choice)
    requestAnimationFrame(() => {
      const first = $('[data-store-picker-close]', modal);
      if (first) first.focus();
    });
    this.dispatch('store:modal-opened', {});
  }

  closeModal() {
    const modal = $('[data-store-picker-modal]', this.root);
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('dss-store-picker--open');
    this.dispatch('store:modal-closed', {});
  }

  isOpen() {
    const modal = $('[data-store-picker-modal]', this.root);
    return !!(modal && modal.classList.contains('is-open'));
  }

  slugFor(id) {
    const store = this.stores.find((s) => s.id === id);
    return store ? store.slug : '';
  }

  dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }
}

let instance = null;

export function initStorePicker(options) {
  if (instance) return instance;
  instance = new StorePicker(options);
  return instance;
}

export function getStorePicker() {
  return instance;
}
